import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BookmarkIcon as BookmarkOutlineIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import type { AppDispatch } from '../store/store';
import {
  loadDictionaryTerms,
  selectDictionaryBookmarkStatus,
  selectDictionaryResult,
  selectDictionaryStatus,
  toggleDictionaryBookmarkThunk,
} from '../features/isoDictionary/isoDictionarySlice';
import type { DictionaryStandardFilter, DictionaryTerm } from '../features/isoDictionary/types';

const STANDARD_FILTERS: Array<{ id: DictionaryStandardFilter; label: string; className: string }> = [
  { id: 'all', label: 'Todas', className: '' },
  { id: 'hls', label: 'HLS común', className: 'bg-amber-100 text-amber-900' },
  { id: 'iso9001', label: 'ISO 9001', className: 'bg-emerald-100 text-emerald-900' },
  { id: 'iso14001', label: 'ISO 14001', className: 'bg-green-100 text-green-900' },
  { id: 'iso45001', label: 'ISO 45001', className: 'bg-red-100 text-red-900' },
  { id: 'iso27001', label: 'ISO 27001', className: 'bg-sky-100 text-sky-900' },
];

const STANDARD_TAG_CLASS: Record<string, string> = {
  iso9001: 'bg-emerald-50 text-emerald-800',
  iso14001: 'bg-green-50 text-green-800',
  iso45001: 'bg-red-50 text-red-800',
  iso27001: 'bg-sky-50 text-sky-800',
  hls: 'bg-amber-50 text-amber-900',
};

const STANDARD_LABEL: Record<string, string> = {
  iso9001: '9001',
  iso14001: '14001',
  iso45001: '45001',
  iso27001: '27001',
  hls: 'HLS',
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function firstLetter(term: DictionaryTerm): string {
  const key = term.sortKey || term.term;
  const match = key.normalize('NFD').replace(/[\u0300-\u036f]/g, '').match(/[A-Z]/i);
  return match ? match[0].toUpperCase() : '#';
}

function groupByLetter(items: DictionaryTerm[]): Map<string, DictionaryTerm[]> {
  const groups = new Map<string, DictionaryTerm[]>();
  for (const item of items) {
    const letter = firstLetter(item);
    const list = groups.get(letter) ?? [];
    list.push(item);
    groups.set(letter, list);
  }
  return groups;
}

export function IsoDictionaryPage() {
  const dispatch = useDispatch<AppDispatch>();
  const [standard, setStandard] = useState<DictionaryStandardFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  const listParams = useMemo(
    () => ({
      standard,
      q: debouncedSearch || undefined,
      pageSize: 200,
    }),
    [standard, debouncedSearch],
  );

  const result = useSelector(selectDictionaryResult(listParams));
  const status = useSelector(selectDictionaryStatus);
  const bookmarkStatus = useSelector(selectDictionaryBookmarkStatus);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    void dispatch(loadDictionaryTerms(listParams));
  }, [dispatch, listParams]);

  const grouped = useMemo(() => groupByLetter(result?.items ?? []), [result?.items]);
  const lettersWithEntries = useMemo(
    () => ALPHABET.filter((letter) => grouped.has(letter)),
    [grouped],
  );

  const handleToggleBookmark = useCallback(
    (termId: string) => {
      if (bookmarkStatus === 'loading') return;
      void dispatch(toggleDictionaryBookmarkThunk(termId));
    },
    [bookmarkStatus, dispatch],
  );

  const scrollToLetter = (letter: string) => {
    setActiveLetter(letter);
    document.getElementById(`dict-letter-${letter}`)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (status === 'loading' && !result) {
    return <p className="p-6 text-sm text-muted-foreground">Cargando diccionario ISO…</p>;
  }

  const items = result?.items ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <header className="space-y-2 border-b border-border pb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ayuda</p>
        <h1 className="text-2xl font-semibold tracking-tight">Diccionario de terminología ISO</h1>
        <p className="text-sm text-muted-foreground">
          {result?.total ?? 0} términos · ISO 9001, 14001, 45001, 27001 y HLS
        </p>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap gap-2">
          {STANDARD_FILTERS.map((filter) => {
            const isActive = standard === filter.id;
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => setStandard(filter.id)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card hover:border-primary/40'
                }`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar término o definición…"
          maxLength={256}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm sm:ml-auto sm:max-w-xs"
        />
      </div>

      {lettersWithEntries.length > 0 && (
        <div className="flex flex-wrap gap-1 border-b border-border pb-3">
          {ALPHABET.map((letter) => {
            const hasEntries = grouped.has(letter);
            return (
              <button
                key={letter}
                type="button"
                disabled={!hasEntries}
                onClick={() => scrollToLetter(letter)}
                className={`flex h-7 w-7 items-center justify-center rounded text-xs font-medium ${
                  hasEntries
                    ? activeLetter === letter
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                    : 'cursor-default text-muted-foreground/40'
                }`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}

      {status === 'failed' && (
        <p className="text-sm text-destructive">No se pudo cargar el diccionario.</p>
      )}

      {items.length === 0 && status === 'succeeded' && (
        <p className="text-sm text-muted-foreground">No hay términos que coincidan con el filtro.</p>
      )}

      <div className="space-y-8">
        {lettersWithEntries.map((letter) => (
          <section key={letter} id={`dict-letter-${letter}`}>
            <h2 className="mb-3 border-b border-border pb-1 text-3xl font-semibold text-muted-foreground/50">
              {letter}
            </h2>
            <ul className="divide-y divide-border">
              {(grouped.get(letter) ?? []).map((term) => {
                const isExpanded = expandedId === term.id;
                return (
                  <li key={term.id} className="py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:gap-6">
                      <div className="sm:w-64 shrink-0">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : term.id)}
                          className="text-left text-base font-semibold hover:text-primary"
                        >
                          {term.term}
                        </button>
                        {term.termEn && (
                          <p className="mt-0.5 text-sm italic text-muted-foreground">{term.termEn}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1">
                          {term.standards.map((std) => (
                            <span
                              key={std}
                              className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                                STANDARD_TAG_CLASS[std] ?? 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {STANDARD_LABEL[std] ?? std}
                            </span>
                          ))}
                        </div>
                        {term.clauseRef && (
                          <p className="mt-1 font-mono text-xs text-muted-foreground">{term.clauseRef}</p>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleBookmark(term.id)}
                          disabled={bookmarkStatus === 'loading'}
                          className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${
                            term.bookmarked ? 'text-emerald-700' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {term.bookmarked ? (
                            <BookmarkSolidIcon className="h-4 w-4" />
                          ) : (
                            <BookmarkOutlineIcon className="h-4 w-4" />
                          )}
                          {term.bookmarked ? 'Guardado' : 'Guardar'}
                        </button>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-relaxed">{term.definition}</p>
                        {isExpanded && (
                          <div className="mt-3 space-y-3 border-l-2 border-border pl-3">
                            {term.whenToUse && (
                              <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                  Cuándo usarlo
                                </p>
                                <p className="mt-1 text-sm leading-relaxed">{term.whenToUse}</p>
                              </div>
                            )}
                            {term.howToApply && (
                              <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                  Cómo aplicarlo
                                </p>
                                <p className="mt-1 text-sm leading-relaxed">{term.howToApply}</p>
                              </div>
                            )}
                            {term.note && (
                              <p className="text-sm italic text-muted-foreground">{term.note}</p>
                            )}
                          </div>
                        )}
                        {!isExpanded && (term.whenToUse || term.howToApply) && (
                          <button
                            type="button"
                            onClick={() => setExpandedId(term.id)}
                            className="mt-2 text-xs font-medium text-primary hover:underline"
                          >
                            Ver detalle
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
